// src/components/PersonalInfo.js
import React from 'react';
import { Box, Typography, Container, Chip } from '@mui/material';
import { Email, LocationOn, LinkedIn, Work } from '@mui/icons-material';
import { useText } from '../context/TextContext';
import profileImage from '../assets/profile.jpg';

const PersonalInfo = () => {
  const textData = useText();
  const personalInfo = textData.personalInfo;
  const commercialRoles = textData.commercialRoles || [];
  const openTo = textData.openTo;
  const experiences = textData.experiences;

  const calculateYearsOfExperience = () => {
    if (!experiences || experiences.length === 0) return '0';

    try {
      let firstJobExperience = null;
      for (let i = experiences.length - 1; i >= 0; i--) {
        const exp = experiences[i];
        if (exp && exp.duration) {
          const startPart = exp.duration.split(/[–-]/)[0].trim();
          if (startPart.includes('/') && startPart.split('/').length === 2) {
            firstJobExperience = exp;
            break;
          }
        }
      }

      if (!firstJobExperience || !firstJobExperience.duration) {
        firstJobExperience = experiences[experiences.length - 1];
      }

      if (!firstJobExperience || !firstJobExperience.duration) return '0';

      const startDateStr = firstJobExperience.duration.split(/[–-]/)[0].trim();

      let month, year;
      if (startDateStr.includes('/')) {
        const dateParts = startDateStr.split('/');
        if (dateParts.length !== 2) return '0';
        month = parseInt(dateParts[0], 10);
        year = parseInt(dateParts[1], 10);
      } else {
        year = parseInt(startDateStr, 10);
        month = 1;
      }

      if (isNaN(year) || (month && isNaN(month))) return '0';

      const startDate = new Date(year, (month || 1) - 1, 1);
      const now = new Date();

      const yearsDiff = now.getFullYear() - startDate.getFullYear();
      const monthsDiff = now.getMonth() - startDate.getMonth();

      const totalYears = monthsDiff < 0 ? yearsDiff - 1 : yearsDiff;
      const totalMonths = monthsDiff < 0 ? 12 + monthsDiff : monthsDiff;

      if (totalMonths >= 6) {
        return `${totalYears + 1}+`;
      }

      return `${totalYears}+`;
    } catch (error) {
      console.error('Error calculating years of experience:', error);
      return '0';
    }
  };

  const yearsOfExperience = calculateYearsOfExperience();

  return (
    <Container id="personal-info">
      <Box sx={{ marginTop: 4, marginBottom: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ marginBottom: 3, color: '#0078D4' }}>Personal Info</Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: { xs: 'center', md: 'flex-start' }
        }}>
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ color: '#0078D4' }} />
              <Typography variant="body1">{personalInfo.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn sx={{ color: '#0078D4' }} />
              <Typography variant="body1">{personalInfo.location}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkedIn sx={{ color: '#0078D4' }} />
              <Typography variant="body1">
                <a href={personalInfo.linkedin} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                  LinkedIn Profile
                </a>
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            flex: 1.4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            justifyContent: 'flex-start',
            width: '100%',
          }}>
            <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 1 }}>
              {personalInfo.name}
            </Typography>
            <Typography variant="h6" sx={{ color: '#605E5C', fontWeight: 400, marginBottom: 1.5 }}>
              {personalInfo.position}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
              <Work sx={{ color: '#0078D4' }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {yearsOfExperience} years of experience
              </Typography>
            </Box>

            {commercialRoles.length > 0 && (
              <Box sx={{ width: '100%', marginBottom: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#605E5C', mb: 1, fontWeight: 600 }}>
                  Focus areas through Aldea Systems
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {commercialRoles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: '2px',
                        borderColor: '#EDEBE9',
                        color: '#242424',
                        backgroundColor: '#FAF9F8',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {openTo && (
              <Typography variant="body2" sx={{ color: '#605E5C', lineHeight: 1.6 }}>
                {openTo}
              </Typography>
            )}
          </Box>

          <Box sx={{
            flex: 1,
            display: 'flex',
            justifyContent: { xs: 'center', md: 'flex-end' }
          }}>
            <Box
              component="img"
              src={profileImage}
              alt="Profile"
              sx={{
                borderRadius: '4px',
                height: '350px',
                border: '1px solid #EDEBE9',
                boxShadow: '0 1.6px 3.6px rgba(0,0,0,0.13), 0 0.3px 0.9px rgba(0,0,0,0.11)',
                objectFit: 'cover',
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PersonalInfo;
